import React, { useEffect, useMemo, useState } from "react";
import Dashboard from "components/dashboard";
import { PageHeaderBar, PageSection } from "components/page";
import { QueryResponseHandler, useQuery } from "components/graphql";
import { getRoomTemplateQuery } from "./details-page";
import { useCurrentCustomerContext } from "components/current-customer-context";
import { EditRoomTemplateFileForm } from "./forms/edit-file-form";
import { useAuthDataContext } from "components/auth";
import { updateTemplateFile } from "../../services/commonService";
import FormCancel from "../../components/form/form-cancel";
import { FormattedMessage, injectIntl } from "react-intl";
import withStyles from "components/with-styles";
import styles from "./styles/file-editor-styles";
import { getEnvironment } from "../../utils/utils";
import { checkEvuService } from "../../services/evu-services";
import { resolveEvuErrorMessage } from "./evu-error-messages";
import { HTTP_CODE_OK } from "../../utils/constants";
import { useLoadingContext } from "../../components/loading-spinner-overlay";
import Alert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import DataGridItem from "../../components/data-grid/data-grid-item";

let file = {};

const RoomTemplateEditFilePage = ({
  match: {
    params: { id, contractId },
  },
  history,
  classes,
  intl,
}) => {
  const { hasAdminPrivileges } = useAuthDataContext();
  const { currentCustomerId } = useCurrentCustomerContext();
  const { setLoading } = useLoadingContext();

  const [initialValues, setInitialValues] = useState({ file: {} });
  const [acceptButtonDisabled, setAcceptButtonDisabled] = useState(false);

  const query = useMemo(
    () => getRoomTemplateQuery({ roomTemplateId: id, customerId: currentCustomerId, contractId, hasAdminPrivileges }),
    [contractId, currentCustomerId, id, hasAdminPrivileges],
  );
  const { data, loading, error } = useQuery(query);

  const roomTemplateToEdit = data ? data.roomTemplate : null;

  const [errorMessage, setErrorMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState(null);
  const [confirmTestSaveOpen, setConfirmTestSaveOpen] = useState(false);

  useEffect(
    () => {
      if (roomTemplateToEdit) {
        setInitialValues({
          file: JSON.parse(roomTemplateToEdit.file),
        });
        file = roomTemplateToEdit.file;
      }
    },
    [roomTemplateToEdit],
  );
  const onHandleSubmit = () => {
    setLoading(true);
    setValidationErrors(null);

    const jsonBlob = new Blob([file], { type: "application/json" });
    const jsonFile = new File([jsonBlob], `${data.roomTemplate.codename}.json`, { type: "application/json" });

    const formData = new FormData();
    formData.append("enviroment", getEnvironment());
    formData.append("file", jsonFile);

    checkEvuService(formData)
      .then(r => {
        if (r.status === HTTP_CODE_OK) {
          updateTemplateFile(
            { body: { file: file }, contractId, roomId: id },
            () => {
              setLoading(false);
              history.goBack();
            },
            () => {
              setLoading(false);
              setErrorMessage(intl.formatMessage({ id: "app.page.roomTemplates.edit.error" }));
            },
          );
        }
      })
      .catch(e => {
        setLoading(false);
        setErrorMessage(intl.formatMessage({ id: "app.page.roomTemplates.validate.error" }));
        if (!!e.response?.data?.detail) {
          setValidationErrors(e.response.data.detail);
        } else {
          setValidationErrors({ msg: intl.formatMessage({ id: "app.page.roomTemplates.validate.error" }) });
        }
        // Test templates: warn and ask for confirmation before saving
        if (data.roomTemplate.test) {
          setConfirmTestSaveOpen(true);
        }
      });
  };

  const handleSkipValidation = () => {
    setLoading(true);
    setValidationErrors(null);

    updateTemplateFile(
      { body: { file: file }, contractId, roomId: id },
      () => {
        setLoading(false);
        history.goBack();
      },
      () => {
        setLoading(false);
        setErrorMessage(intl.formatMessage({ id: "app.page.roomTemplates.edit.error" }));
      },
    );
  };

  const handleChange = event => {
    const { json, error } = event;

    setAcceptButtonDisabled(true);

    if (!error && json) {
      setAcceptButtonDisabled(false);
      file = json;
    }
  };

  return (
    <>
      <Dashboard>
        <QueryResponseHandler error={error} loading={loading} elements={roomTemplateToEdit}>
          <PageHeaderBar
            showBackButton
            title="app.page.roomTemplate.edit"
            titleParams={{ roomTemplateName: roomTemplateToEdit ? roomTemplateToEdit.codename : "" }}
          />
          <PageSection>
            <EditRoomTemplateFileForm values={initialValues} handleChange={handleChange} />
            <DataGridItem big>
              <div className={classes.validationErrorContainer}>
                {!!validationErrors && (
                  <Typography>{intl.formatMessage({ id: "app.page.roomTemplates.error" })}</Typography>
                )}
                {Array.isArray(validationErrors) ? (
                  validationErrors.map((error, index) => (
                    <Typography key={index} variant="body2" color="error">
                      {resolveEvuErrorMessage(error.msg ?? (typeof error === "string" ? error : JSON.stringify(error)), intl)}
                    </Typography>
                  ))
                ) : validationErrors?.msg ? (
                  <Typography variant="body2" color="error">
                    {resolveEvuErrorMessage(validationErrors.msg, intl)}
                  </Typography>
                ) : validationErrors && typeof validationErrors === "string" ? (
                  <Typography variant="body2" color="error">
                    {resolveEvuErrorMessage(validationErrors, intl)}
                  </Typography>
                ) : null}
              </div>
            </DataGridItem>
            <div className={classes.buttonsContainer}>
              <FormCancel onClick={() => history.goBack()}>
                <FormattedMessage id="common.cancel" />
              </FormCancel>
              <FormCancel onClick={onHandleSubmit} disabled={acceptButtonDisabled}>
                <FormattedMessage id="common.accept" />
              </FormCancel>
            </div>
          </PageSection>
        </QueryResponseHandler>
        <Snackbar
          open={!!errorMessage}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={6000}
          onClose={() => setErrorMessage("")}
        >
          <Alert onClose={() => setErrorMessage("")} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      </Dashboard>
      <Dialog open={confirmTestSaveOpen} onClose={() => setConfirmTestSaveOpen(false)}>
        <DialogTitle>
          <FormattedMessage id="app.page.roomTemplates.testSaveConfirm.title" defaultMessage="Plantilla con errores EVU" />
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: "1rem" }}>
            <FormattedMessage
              id="app.page.roomTemplates.testSaveConfirm.body"
              defaultMessage="La plantilla tiene errores de validación EVU. Al ser una plantilla de prueba puedes guardarla igualmente, pero ten en cuenta que podría no funcionar correctamente."
            />
          </Typography>
          {validationErrors && (
            <Alert severity="warning" style={{ marginTop: "0.5rem" }}>
              {Array.isArray(validationErrors) ? (
                validationErrors.map((e, i) => (
                  <Typography key={i} variant="body2">
                    {resolveEvuErrorMessage(e.msg ?? (typeof e === "string" ? e : JSON.stringify(e)), intl)}
                  </Typography>
                ))
              ) : validationErrors?.msg ? (
                <Typography variant="body2">{resolveEvuErrorMessage(validationErrors.msg, intl)}</Typography>
              ) : typeof validationErrors === "string" ? (
                <Typography variant="body2">{resolveEvuErrorMessage(validationErrors, intl)}</Typography>
              ) : null}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTestSaveOpen(false)}>
            <FormattedMessage id="common.cancel" />
          </Button>
          <Button
            color="primary"
            onClick={() => {
              setConfirmTestSaveOpen(false);
              handleSkipValidation();
            }}
          >
            <FormattedMessage id="app.page.roomTemplates.testSaveConfirm.confirm" defaultMessage="Guardar igualmente" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default injectIntl(withStyles(styles)(RoomTemplateEditFilePage));
